import { Column } from '@ant-design/charts';
import { formatDate, formatDateTimeChart, useTranslation } from '@threatadvisor/react-utils';
import { Spin, Typography } from 'antd';
import React, { ReactElement, useEffect, useId } from 'react';
import { ResultSet } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { getPriorityScaleColors } from '../../utils';
import { VizState } from '@threatadvisor/react-components';

export type AverageTimeToEscalationByPriorityProps = {
  clientUid: string;
  reportStart: string;
  reportEnd: string;
  securityEventApi: string;
};

export default function AverageTimeToEscalationByPriority(
  props: AverageTimeToEscalationByPriorityProps
): ReactElement {
  const { clientUid, reportStart, reportEnd } = props;

  const { t } = useTranslation('translation');

  const getQuery = (): VizState => {
    return {
      chartType: 'bar',
      pivotConfig: {
        x: ['EscalatedDetections.priority'],
        y: ['measures'],
        fillMissingDates: true
      },
      query: {
        measures: ['EscalatedDetections.avg_minutes_to_escalation'],
        dimensions: ['EscalatedDetections.priority'],
        timeDimensions: [
          {
            dimension: 'EscalatedDetections.created_date',
            dateRange: [reportStart, reportEnd]
          }
        ],
        order: {
          'EscalatedDetections.priority': 'asc'
        },
        filters: [
          {
            member: 'EscalatedDetections.priority',
            operator: 'notContains',
            values: ['P5']
          },
          {
            member: 'EscalatedDetections.client_uid',
            operator: 'equals',
            values: [clientUid]
          }
        ]
      }
    };
  };
  const overallAverageQuery = (): VizState => {
    return {
      chartType: 'number',
      pivotConfig: {
        x: [],
        y: ['measures'],
        fillMissingDates: true
      },
      query: {
        measures: ['EscalatedDetections.count', 'EscalatedDetections.avg_minutes_to_escalation'],
        timeDimensions: [
          {
            dimension: 'EscalatedDetections.created_date',
            dateRange: [reportStart, reportEnd]
          }
        ],
        order: {
          'EscalatedDetections.priority': 'asc'
        },
        filters: [
          {
            member: 'EscalatedDetections.priority',
            operator: 'notContains',
            values: ['P5']
          },
          {
            member: 'EscalatedDetections.client_uid',
            operator: 'equals',
            values: [clientUid]
          }
        ]
      }
    };
  };

  const { resultSet: timeToEscalateData } = useCubeQuery(getQuery().query!);
  const { resultSet: averageData } = useCubeQuery(overallAverageQuery().query!);

  function getData(resultSet: ResultSet<any>) {
    const results = resultSet.chartPivot(getQuery().pivotConfig);
    const data: { x: string; y: number; series: string }[] = [];
    let seriesName = '';
    resultSet.seriesNames(getQuery().pivotConfig).forEach(({ key }) => {
      seriesName = key;
      data.push(
        ...results.map(result => ({
          x: t(result.x),
          y: result[key],
          series: key
        }))
      );
    });

    //include p1-p4
    if (!data.find(e => e.x === 'P1')) data.push({ x: 'P1', y: 0, series: seriesName });
    if (!data.find(e => e.x === 'P2')) data.push({ x: 'P2', y: 0, series: seriesName });
    if (!data.find(e => e.x === 'P3')) data.push({ x: 'P3', y: 0, series: seriesName });
    if (!data.find(e => e.x === 'P4')) data.push({ x: 'P4', y: 0, series: seriesName });

    return data.sort((a, b) => a.x.localeCompare(b.x));
  }

  function getAverageData(resultSet: ResultSet<any>) {
    return formatDateTimeChart(
      resultSet.chartPivot()[0]['EscalatedDetections.avg_minutes_to_escalation'],
      false
    );
  }

  const myId = useId();
  useEffect(() => {
    if (!window['chartRenders']) window['chartRenders'] = {};
    window['chartRenders'][myId] = 'Rendering';
    return () => {
      window['chartRenders'] && delete window['chartRenders'][myId];
    };
  }, []);

  return (
    <>
      <h2 className="text-uppercase">Average Time To Escalation by Priority Level</h2>

      <figure>
        {' '}
        <Typography.Title level={4}>
          Overall Average Time to Escalation: {averageData ? getAverageData(averageData) : <Spin />}
        </Typography.Title>
      </figure>
      <figure>
        {' '}
        {timeToEscalateData ? (
          <Column
            {...{
              style: { maxWidth: 50 },
              colorField: 'x',

              legend: false,
              label: {
                // AT EACH Y POINT
                formatter: val => formatDateTimeChart(val, false),
                text: val => (val.y === 0 ? '' : val.y),
                textBaseline: 'bottom',
                style: {
                  becomeWrap: true,
                  wordWrapWidth: 40,
                  wordWrap: true
                }
              },
              ...getPriorityScaleColors(),
              theme: {
                type: 'threatadvisor'
              },
              tooltip: false,
              axis: {
                // ACROSS THE Y Axis... vertical in this case
                y: {
                  labelFormatter: (text: string, item: any, index: number) => {
                    return formatDateTimeChart(text, true);
                  }
                }
              }
            }}
            data={getData(timeToEscalateData)}
            yField="y"
            xField="x"
            seriesField="series"
          />
        ) : (
          <Spin />
        )}
        <figcaption>
          <Typography.Text type="secondary">
            Shows average time to escalation by priority level between{' '}
            {formatDate(new Date(reportStart))} and {formatDate(new Date(reportEnd))}.
          </Typography.Text>
        </figcaption>
      </figure>
    </>
  );
}
